import { useContext, useEffect, useMemo } from 'react';

import { randomStr } from '../../../utils/helper';
import { RadioGroupContext } from './Context';

export interface Props {
  /**
   * canUncheck only has an impact when user clicks on already checked radio option.
   *
   * when `canUncheck === true` then clicking on checked radio option will uncheck it.
   * `else` clicking on checked radio option will not have any impact
   *
   * @default false
   */
  canUncheck?: boolean;
  defaultValue?: string | null;
  value?: string | null;
  onChange?: (value: string | null) => void;
}

const useRadioGroupVM = ({ canUncheck = false, defaultValue = null, value = null, onChange }: Props) => {
  const labelId = useMemo(() => `radio-group-label-${randomStr(4)}`, []);

  const { setCanUncheck, value: contextValue, setValue: setContextValue } = useContext(RadioGroupContext);

  const handleChange = (newValue: string | null) => {
    onChange?.(newValue);
  };

  useEffect(
    function setInitialCanUncheck() {
      setCanUncheck(canUncheck);
    },
    [canUncheck, setCanUncheck]
  );

  useEffect(
    function setDefaultValue() {
      setContextValue(defaultValue);
    },
    [defaultValue, setContextValue]
  );

  useEffect(
    function setInitialValue() {
      setContextValue(value);
    },
    [value, setContextValue]
  );

  return { labelId, contextValue, handleChange };
};

export default useRadioGroupVM;
